import { View } from "@/components/ui/view";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { Button } from "@/components/ui/button";
import { Pressable } from "@/components/ui/pressable";
import { KeyboardAvoidingView } from 'react-native';
import { Box } from "@/components/ui/box";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import i18n from 'i18n-js';
import { TouchableOpacity } from 'react-native';
import React, { useEffect, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import platzhalter from "@/assets/icons/icon_blue_rounded_512x512.png"

import Footer from "@/components/basic/Footer";
import ItemInput from '@/components/basic/ItemInput';
import TimerInput from "@/components/basic/TimerInput";
import MediaCarousell from "@/components/media/MediaCarousell";
import DeletableListElement from '@/components/workflow/DeletableListElement';
import ListElement from '@/components/workflow/ListElement';
import useIsMounted from '@/hooks/mounted';
import { imageSrc } from "@/components/media/MediaSelector";
import ModalStepType from "@/screens/modals/ModalStepType";
import { switchScreen } from '@/hooks/navigate';
import useRefreshEffect from '@/hooks/refreshed';
import { Platform } from 'react-native';

const dummyTgt = { title: "Platzhalter", text: i18n.t('stepEdit.dummyTarget'), media: [platzhalter], type: -1 }
const dummyReg = { title: "Platzhalter", text: i18n.t('stepEdit.dummyRegular'), media: [platzhalter], type: -1 }

function Details({ navigation, updateTimer, deleteItem, steps, idx }) {
    const icons = useSelector(state => state.icons)

    switch (steps[idx]?.type) {
        case 1:
            return (steps[idx].check.map((item, i) =>
                <TouchableOpacity key={item.title + i} onPress={() => { switchScreen(navigation, 'ItemEdit', { items: steps[idx].check, idx: i }) }} className="px-1 bg-secondary-50 h-[80px] active:bg-primary-300">
                    <DeletableListElement idx={i} element={item} onDelete={deleteItem} />
                </TouchableOpacity>));
        case 2:
            return (
                <VStack space='xs' className="items-center">
                    <Text bold className="text-black text-2xl">{i18n.t('stepEdit.timerInfo')}</Text>
                    <TimerInput timer={steps[idx].timer | 0} updateTimer={updateTimer} />
                </VStack>
            );
        case 3:
            const topStep = steps[idx].no > -1 ? steps[steps[idx].no] || dummyReg : steps[idx + 1] || dummyTgt
            const botStep = steps[idx].yes > -1 ? steps[steps[idx].yes] || dummyReg : steps[idx + 1] || dummyTgt
            const topActive = steps[idx].no == -1 && steps[idx].yes > idx || steps[idx].yes == -1 && steps[idx].no < idx
            const botActive = !topActive || Math.max(steps[idx].no ?? -1, steps[idx].yes ?? -1) == -1
            return (
                <VStack space='xs'>
                    <Text bold className="text-black px-3 text-lg mt-2">{i18n.t('stepEdit.selectTarget')}</Text>
                    <Button variant='subtle' disabled={!topActive} onPress={() => { switchScreen(navigation, 'DecisionEdit', { steps: steps, idx: idx, onYes: false }) }} className="px-3 bg-secondary-50 h-[80px] active:bg-primary-300">
                        <ListElement key={topStep.media[0]} element={topStep} avatar={icons.types[topStep.type]} onNo={true} disabled={!topActive} />
                    </Button>
                    <Text bold className="text-black text-lg self-center mt-2">{i18n.t('stepEdit.orText')}</Text>
                    <Button variant='subtle' disabled={!botActive} onPress={() => { switchScreen(navigation, 'DecisionEdit', { steps: steps, idx: idx, onYes: true }) }} className="px-3 bg-secondary-50 h-[80px] active:bg-primary-300">
                        <ListElement key={botStep.media[0]} element={botStep} avatar={icons.types[botStep.type]} onYes={true} disabled={!botActive} />
                    </Button>
                </VStack>
            );
        case 9:
            return (steps[idx].check.map((item, i) => <Text key={'C' + i} className="text-black">{item.title}</Text>));
    }
    return <></>

}

export let updateType
export let updateItem
export let updateDecison

export default function StepEdit({ route, navigation, updateStep, updateExtraStep }) {
    const { steps, idx, name } = route.params
    const mounted = useIsMounted()
    const [actIdx, setActIdx] = useState(idx)
    const [actSteps, setActSteps] = useState(steps)
    const [step, setStep] = useState(actSteps[actIdx] || { title: i18n.t('itemInput.title'), text: i18n.t('itemInput.text'), media: [], type: -1 })
    const [showModal, setShowModal] = useState(false)
    const icons = useSelector((state) => state.icons)
    const insets = useSafeAreaInsets();

    const actions = [
        { icon: 'keyboard-arrow-up', text: idx == 0 ? 'footer.backTask' : 'footer.prevStep', component: idx == 0 ? 'BACK' : 'StepEdit', params: { idx: idx - 1, name: name } },
        { icon: 'home', text: 'footer.home', component: 'Home' },
        { icon: idx >= actSteps.length - 1 ? 'check' : 'keyboard-arrow-down', text: idx >= actSteps.length - 1 ? 'footer.create' : 'footer.nextStep', component: idx >= actSteps.length - 1 ? 'BACK' : 'StepEdit', params: { idx: idx + 1, name: name } }
    ]

    // displayed idx changed => update of local state 
    useRefreshEffect(() => {
        if (idx != actIdx) {
            // console.log('idx changed, save local step', actIdx, idx)
            // Update current step to workflow
            if (name == 'ExtraEdit') {
                // Update current step to extra steps
                updateExtraStep(step, actIdx)
            } else {
                updateStep(step, actIdx)
            }
            //switch to new idx and steps
            setActIdx(idx)
            setStep(actSteps[idx] || { title: i18n.t('itemInput.title'), text: i18n.t('itemInput.text'), media: [], type: -1 })
        }
    }, [idx])

    // displayed step changed => update of local state 
    useRefreshEffect(() => {
        if (step.type < 0) {
            setShowModal(true)
        }
        // console.log('step changed, save local step', actIdx)
        navigation.setOptions({ title: i18n.t(name == 'ExtraEdit' ? 'showStep.extra' : 'showStep.title') + ' ' + (actIdx + 1) + ': ' + stepTypeName(step.type), info: step.title })
        setActSteps(prevSteps => {
            let newSteps = [...prevSteps]
            newSteps[actIdx] = step
            return newSteps
        })
    }, [step])

    // local state triggers update of local task state on unmount
    useEffect(() => {
        return () => {
            if (!mounted()) {
                console.log('exit ' + name + ', save workflow step', idx)
                if (name == 'ExtraEdit') {
                    // Update current step to extra steps
                    updateExtraStep(step, idx)
                } else {
                    updateStep(step, idx)
                }
            }
        };
    }, [step, mounted])

    const updateMedia = uris => {
        setStep(prevStep => (
            { ...prevStep, media: uris }
        ))
    }

    const updateTitle = text => {
        setStep(prevStep => (
            { ...prevStep, title: text }
        ))
    }

    const updateText = text => {
        setStep(prevStep => (
            { ...prevStep, text: text }
        ))
    }

    updateItem = (item, idx) => {
        setStep(prevStep => {
            let check = [...prevStep.check]
            check[idx] = item
            return { ...prevStep, check: check }
        })
    }

    updateDecison = (tgtNew, extraSteps, onYes) => {
        // console.log('UPD', tgtNew, extraSteps)
        // calc number of previous extra steps
        const extraCnt = Math.max(0, Math.max(step.no ?? -1, step.yes ?? -1) - idx - 1)
        let newSteps
        setActSteps(prevSteps => {
            newSteps = [...prevSteps]
            // insert extra steps after current step after deleting previous extra steps
            newSteps.splice(idx + 1, extraCnt, ...extraSteps)
            // console.log(extraCnt, prevSteps, newSteps)
            return newSteps
        })
        updateStep(newSteps, 0)
        setStep(prevStep => {
            if (tgtNew > actIdx) {
                return onYes ? { ...prevStep, yes: -1, no: tgtNew } : { ...prevStep, yes: tgtNew, no: -1 }
            } else {
                return onYes ? { ...prevStep, yes: tgtNew, no: -1 } : { ...prevStep, yes: -1, no: tgtNew }
            }
        })
    }

    const updateTimer = value => {
        setStep(prevStep => (
            { ...prevStep, timer: value }
        ))
    }

    updateType = (type) => {
        setStep(prevStep => {
            switch (type) {
                case 1:
                    return { ...prevStep, type: 1, check: [] }
                case 2:
                    return { ...prevStep, type: 2, timer: 0 }
                case 3:
                    return { ...prevStep, type: 3, yes: -1, no: -1 }
                default:
                    return { ...prevStep, type: 0 }
            }
        })
    }

    const stepTypeName = (type) => {
        switch (type) {
            case 0:
                return i18n.t('stepType.do')
            case 1:
                return i18n.t('stepType.fetch')
            case 2:
                return i18n.t('stepType.wait')
            case 3:
                return i18n.t('stepType.decide')
            default:
                return ''
        }
    }

    const deleteItem = (delIdx) => {
        // TODO: delete of images necessary?
        setStep(prevStep => {
            let check = [...prevStep.check]
            check.splice(delIdx, 1)
            return { ...prevStep, check: check }
        })
    }

    const addStep = () => {
        switchScreen(navigation, 'StepEdit', { idx: idx + 1, name: name })
    }

    const itemEdit = () => {
        switchScreen(navigation, 'ItemEdit', { items: step.check, idx: step.check.length })
    }

    const switchAnswer = () => {
        setStep(prevStep => ({ ...prevStep, no: prevStep.yes, yes: prevStep.no }))
    }

    const button1 = idx >= actSteps.length - 1 ? { action: addStep, info: name == 'ExtraEdit' ? 'decisionEdit.addExtra' : 'footer.newStep' } : null
    const button2 = step.type == 1 ? { action: itemEdit, info: 'stepEdit.addItem' } : step.type == 3 && actSteps[idx] && Math.max(actSteps[idx].no ?? -1, actSteps[idx].yes ?? -1) > -1 ? { action: switchAnswer, info: 'stepEdit.switchAnswer' } : null

    return (
        <View className="bg-secondary-50 flex-1">
            <KeyboardAvoidingView behavior={Platform.select({ android: undefined, ios: 'padding' })} >
                <ScrollView contentContainerStyle={idx >= actSteps.length - 1 || step.type == 1 || step.type == 3 ? { paddingBottom: 120 + insets.bottom } : { paddingBottom: 70 + insets.bottom }}>
                    <MediaCarousell key={actIdx} uris={step.media} isEdit={true} update={updateMedia} />
                    <VStack className="mx-3 mt-3 mb-[100px]">
                        <View >
                            <Box className="border-2 border-white rounded-full absolute right-3 -top-7">
                                <Pressable onPress={() => switchScreen(navigation, 'StepTypeInformation', { step: step })}>
                                    <Avatar size='md'>
                                        <AvatarImage key={step.type} source={imageSrc(icons.helps && icons.helps[step.type])} alt={i18n.t('global.missingImage')} className="border-2 border-primary-900 bg-secondary-50 z-100" />
                                    </Avatar>
                                </Pressable>
                            </Box>
                        </View>
                        <ItemInput updateTitle={updateTitle} updateText={updateText} item={step} infoTitle='infoTitleStep' infoText={step.type == 3 ? 'infoFrageStep' : 'infoDescriptionStep'} />
                        <Details key={actIdx} navigation={navigation} steps={actSteps} idx={actIdx} updateTimer={updateTimer} deleteItem={deleteItem} />
                    </VStack>
                </ScrollView>
            </KeyboardAvoidingView>
            <ModalStepType name={name} showModal={showModal} setShowModal={setShowModal} updateType={updateType} />
            <Footer navigation={navigation} actions={actions} button={button1} button2={button2} />
        </View>
    );
}